import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const RECONNECT_DELAY_MS = 3000;
const MAX_BUFFER_S = 5;   // live view — 5s is enough, 30s wastes RAM
const MAX_RETRIES = 8;    // after 8 failures give up (prevents infinite loops)
// Baseline Level 3.1 — matches ffmpeg -profile:v baseline -level:v 3.1
const MIME_TYPE = 'video/mp4; codecs="avc1.42E01F"';

export const MSEPlayer = ({ camera, onStateChange }) => {
    const { token } = useAuth();
    const videoRef = useRef(null);
    const wsRef = useRef(null);
    const msRef = useRef(null);
    const sbRef = useRef(null);
    const queueRef = useRef([]);
    const isMountedRef = useRef(true);
    const reconnectTimerRef = useRef(null);
    const retryCountRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;
        retryCountRef.current = 0;

        let lastAppendBytes = 0;
        let firstSegmentReceived = false;

        const appendNext = () => {
            const sb = sbRef.current;
            if (!sb || sb.updating || queueRef.current.length === 0) return;
            const chunk = queueRef.current.shift();
            lastAppendBytes = chunk.byteLength;
            try {
                sb.appendBuffer(chunk);
            } catch (e) {
                console.error(`[MSE] appendBuffer DOMException: ${e.name} — ${e.message} (${chunk.byteLength} bytes)`);
            }
        };

        const cleanup = () => {
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            const ws = wsRef.current;
            wsRef.current = null;
            if (ws) {
                ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
                try { ws.close(); } catch (_) {}
            }
            sbRef.current = null;
            queueRef.current = [];
            lastAppendBytes = 0;
            firstSegmentReceived = false;
            const ms = msRef.current;
            msRef.current = null;
            if (ms && ms.readyState === 'open') {
                try { ms.endOfStream(); } catch (_) {}
            }
        };

        const connect = () => {
            if (!isMountedRef.current) return;
            cleanup();

            const video = videoRef.current;
            if (!video) return;

            if (video.src?.startsWith('blob:')) {
                const old = video.src;
                video.src = '';
                URL.revokeObjectURL(old);
            }

            if (!window.MediaSource) {
                console.error('[MSE] MediaSource API not available in this browser');
                if (onStateChange) onStateChange('error');
                return;
            }

            if (!MediaSource.isTypeSupported(MIME_TYPE)) {
                console.error(`[MSE] MIME type not supported: ${MIME_TYPE}`);
                if (onStateChange) onStateChange('error');
                return;
            }

            const ms = new MediaSource();
            msRef.current = ms;
            video.src = URL.createObjectURL(ms);

            ms.addEventListener('sourceopen', () => {
                console.log(`[MSE] MediaSource open for camera ${camera.id}, readyState=${ms.readyState}`);

                let sb;
                try {
                    sb = ms.addSourceBuffer(MIME_TYPE);
                    console.log(`[MSE] SourceBuffer created: ${MIME_TYPE}`);
                } catch (e) {
                    console.error(`[MSE] addSourceBuffer DOMException: ${e.name} — ${e.message}`);
                    return;
                }
                sbRef.current = sb;

                sb.addEventListener('updateend', () => {
                    console.debug(`[MSE] appendBuffer success (${lastAppendBytes} bytes)`);
                    // Trim old content to prevent memory growth
                    try {
                        if (sb.buffered.length > 0 && !sb.updating) {
                            const start = sb.buffered.start(0);
                            const end = sb.buffered.end(sb.buffered.length - 1);
                            if (end - start > MAX_BUFFER_S) {
                                sb.remove(start, end - MAX_BUFFER_S / 2);
                                return; // updateend fires again after remove
                            }
                        }
                    } catch (_) {}
                    appendNext();
                });

                sb.addEventListener('error', (e) => {
                    console.error(`[MSE] SourceBuffer error event: type=${e.type}`, e);
                });

                // Force play — autoPlay alone is sometimes insufficient with MSE blob URLs
                video.play().catch(e => console.warn('[MSE] video.play() rejected:', e.name, e.message));

                const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                const tokenPart = token ? `?token=${encodeURIComponent(token)}` : '';
                const wsUrl = `${protocol}//${window.location.host}/api/cameras/${camera.id}/mse${tokenPart}`;

                const ws = new WebSocket(wsUrl);
                ws.binaryType = 'arraybuffer';
                wsRef.current = ws;

                ws.onopen = () => {
                    console.log(`[MSE] WS connected for camera ${camera.id}`);
                    // NOTE: do NOT reset retryCountRef here.
                    // The WS handshake succeeds even when ffmpeg then fails to reach
                    // the camera — resetting here would cause an infinite retry loop.
                    // Counter resets only when the first real video segment arrives.
                };

                ws.onmessage = (event) => {
                    if (!isMountedRef.current) return;
                    const buf = event.data;
                    const bytes = buf.byteLength;
                    // Peek at box type (bytes 4-7) to label init vs media segment
                    let segLabel = 'unknown';
                    if (bytes >= 8) {
                        const typeBytes = new Uint8Array(buf, 4, 4);
                        const boxType = String.fromCharCode(...typeBytes);
                        segLabel = boxType === 'ftyp' ? 'init(ftyp+moov)' : boxType === 'moof' ? 'media(moof+mdat)' : boxType;
                    }
                    if (!firstSegmentReceived) {
                        console.log(`[MSE] First segment received: ${bytes} bytes type=${segLabel}`);
                        firstSegmentReceived = true;
                        // Real video data arrived — the stream is working, reset retry counter
                        retryCountRef.current = 0;
                    } else {
                        console.debug(`[MSE] WS segment: ${bytes} bytes type=${segLabel}`);
                    }
                    queueRef.current.push(buf);
                    appendNext();
                };

                ws.onclose = (e) => {
                    if (!isMountedRef.current) return;
                    console.warn(`[MSE] WS closed for camera ${camera.id}: code=${e.code} wasClean=${e.wasClean}`);
                    const attempt = retryCountRef.current;
                    if (attempt >= MAX_RETRIES) {
                        console.error(`[MSE] camera ${camera.id}: max retries (${MAX_RETRIES}) exhausted — falling back to JPEG.`);
                        if (onStateChange) onStateChange('error');
                        return;
                    }
                    retryCountRef.current = attempt + 1;
                    const delay = Math.min(RECONNECT_DELAY_MS * Math.pow(2, attempt), 30_000);
                    console.log(`[MSE] camera ${camera.id}: retry ${attempt + 1}/${MAX_RETRIES} in ${delay}ms`);
                    reconnectTimerRef.current = setTimeout(() => {
                        if (isMountedRef.current) connect();
                    }, delay);
                };

                ws.onerror = (e) => {
                    console.warn(`[MSE] WS error for camera ${camera.id}:`, e.type, e);
                };
            });
        };

        connect();

        return () => {
            isMountedRef.current = false;
            cleanup();
            const video = videoRef.current;
            if (video?.src?.startsWith('blob:')) {
                URL.revokeObjectURL(video.src);
            }
        };
    }, [camera.id, token]);

    return (
        <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-contain"
            autoPlay
            muted
            playsInline
        />
    );
};
